/*
 * Canonical membership plan config — the SINGLE source of truth for plan
 * names and prices on the public marketing surface.
 *
 * Why this exists: the old landing page hardcoded "$49" while the app showed
 * other numbers, so the two could (and did) silently diverge. The marketing
 * landing now reads names/prices from here only, and white-label tenants
 * override at runtime via GET /api/tenant/plans (same endpoint the in-app
 * /pricing page uses) — see useLandingPlans().
 *
 * NOTE: the in-app /pricing (Membership.js) still renders its own legacy
 * basic/pro/elite tiers wired to Stripe price IDs. Unifying that billing flow
 * onto these keys touches Stripe and is intentionally a separate change; this
 * module is the canonical source the landing consumes today.
 *
 * ALSO: email templates quote these prices, and the backend cannot require this
 * ES module — so `src/backend/config/publicPricing.js` mirrors the numbers.
 * Change a price here and change it there too; the two are pinned together by
 * tests/backend/publicPricing.test.js, which fails if they drift.
 */

// Order the cards render in.
export const PLAN_ORDER = ['essentials', 'professional', 'enterprise'];

export const PLANS = {
  essentials: {
    key: 'essentials',
    name: 'Essentials',
    price: 29,
    currency: 'usd',
    interval: 'month',
    tagline: 'For traders getting started with market intelligence.',
    features: [
      'All 8 workspaces',
      '90+ panels',
      'Live charts with indicators',
      'Watchlist & price alerts',
      'Intelligence Wire',
      'News desk & regional feeds',
      'Economic calendar',
      'Trading Floor Discord access',
    ],
    cta: 'Start Free Trial',
    ctaHref: '/signup?plan=essentials',
    ctaVariant: 'ghost',
    featured: false,
  },
  professional: {
    key: 'professional',
    name: 'Professional',
    price: 49,
    currency: 'usd',
    interval: 'month',
    tagline: 'Everything. For traders who mean business.',
    features: [
      'Everything in Essentials',
      'Flow sentiment & multi-timeframe',
      'Market radar & positioning',
      'Options flow & whale tracking',
      'AI Research Assistant',
      'Portfolio Intelligence with AI briefs',
      'Country Instability Index',
      'Global Risk Map (3D)',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    ctaHref: '/signup?plan=professional',
    ctaVariant: 'primary',
    featured: true,
    highlightLabel: 'MOST POPULAR',
  },
  enterprise: {
    key: 'enterprise',
    name: 'Enterprise',
    // No numeric price — shown as "Custom".
    priceLabel: 'Custom',
    tagline: 'For funds, prop firms, and trading communities.',
    features: [
      'Everything in Professional',
      'White-label deployment',
      'Custom tenant branding',
      'API access',
      'Data export',
      'Dedicated account manager',
      'Priority infrastructure',
      'SLA guarantees',
    ],
    cta: 'Contact Sales',
    ctaHref: '/contact?topic=enterprise',
    ctaVariant: 'ghost',
    featured: false,
  },
};

const CURRENCY_SYMBOLS = { usd: '$', eur: '€', gbp: '£' };

/** Symbol for a currency code (default "$"). */
export function currencySymbol(currency = 'usd') {
  return CURRENCY_SYMBOLS[String(currency).toLowerCase()] || `${String(currency).toUpperCase()} `;
}

const INTERVAL_SUFFIX = { month: '/mo', year: '/yr', week: '/wk' };

/** Short period suffix for a plan interval (default "/mo"). */
export function intervalSuffix(interval = 'month') {
  return INTERVAL_SUFFIX[interval] || '/mo';
}

/** Ordered list of canonical plan objects. */
export function getPlans() {
  return PLAN_ORDER.map((key) => PLANS[key]).filter(Boolean);
}

/** The professional plan price, used by the comparison table so the two
 *  landing prices can never disagree. Returns e.g. "$49". */
export function professionalPriceLabel() {
  const p = PLANS.professional;
  return `${currencySymbol(p.currency)}${p.price}`;
}
